import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { 
  useTeamActive,
  useCreateUser,
  useUpdateUser,
  useDeleteUser,
  useTeamMembers,
  usePendingUsers,
  useDisabledUsers,
  useInviteUser,
  useUpdateUserRole,
  useToggleUserStatus,
  useApproveUser,
  useReapproveUser,
  useResendInvitation,
  useResendVerification
} from '../hooks/api';
import { 
  Users, 
  UserPlus, 
  Crown, 
  Shield, 
  User,
  Search,
  X as XIcon,
  Mail,
  UserX
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ConfirmModal from './ConfirmModal';
import FormDrawer from './FormDrawer';
import { getErrorMessage } from '../utils/errorUtils';
import NowPlaying from './spotify/NowPlaying';

const TeamPage = () => {
  const { user } = useAuth();
  const canViewTeam = user?.role === 'admin' || user?.role === 'project_lead';
  const isAdmin = user?.role === 'admin';
  
  // Team endpoints require VIEW_TEAM (admin/lead); disabled users are admin-only
  const { 
    data: teamActiveData, 
    isLoading: teamActiveLoading,
    refetch: refetchTeamActive,
  } = useTeamActive({ enabled: canViewTeam });
  
  const { 
    data: teamMembersData, 
    isLoading: teamMembersLoading,
    refetch: refetchTeamMembers,
  } = useTeamMembers({ enabled: canViewTeam });
  
  const { 
    data: pendingUsersData, 
    isLoading: pendingUsersLoading,
    refetch: refetchPendingUsers,
  } = usePendingUsers({ enabled: canViewTeam });
  
  const { 
    data: disabledUsersData, 
    isLoading: disabledUsersLoading,
    refetch: refetchDisabledUsers,
  } = useDisabledUsers({ enabled: isAdmin });

  const refreshTeamLists = async () => {
    const jobs = [refetchTeamMembers(), refetchPendingUsers(), refetchTeamActive()];
    if (isAdmin) jobs.push(refetchDisabledUsers());
    await Promise.all(jobs);
  };
  
  // Mutations
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const inviteUserMutation = useInviteUser();
  const updateUserRoleMutation = useUpdateUserRole();
  const toggleUserStatusMutation = useToggleUserStatus();
  const approveUserMutation = useApproveUser();
  const reapproveUserMutation = useReapproveUser();
  const resendInvitationMutation = useResendInvitation();
  const resendVerificationMutation = useResendVerification();
  
  // Extract data from React Query responses
  const teamMembers = teamMembersData || [];
  const teamActive = teamActiveData || [];
  const pendingUsers = pendingUsersData || [];
  const disabledUsers = disabledUsersData || [];
  
  // Combined loading state
  const loading = canViewTeam && (teamActiveLoading || teamMembersLoading || pendingUsersLoading || (isAdmin && disabledUsersLoading));
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({
    emails: [],
    roles: ['member'] // Default to member role
  });
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'OK'
  });
  const [isInviting, setIsInviting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [togglingUserId, setTogglingUserId] = useState(null);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [reapprovingUserId, setReapprovingUserId] = useState(null);
  const [approvingUserId, setApprovingUserId] = useState(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Filter state for summary cards
  const [activeFilter, setActiveFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20, 50];

  // Separate users into categories for frontend filtering
  const verifiedUsers = teamMembers.filter(member => member.is_verified);
  const pendingInvitations = teamMembers.filter(member => !member.is_verified && member.invite_token); // Include both active and expired invitations
  const pendingEmailVerification = teamMembers.filter(member => !member.is_verified && !member.invite_token && !member.invitation_expired);
  const unverifiedUsers = teamMembers.filter(member => !member.is_verified && !member.invite_token);

  // Filter and search logic
  const filteredMembers = teamMembers.filter(member => {
    // For default "all" view, only show verified users (exclude expired invitations and pending email verification)
    if (activeFilter === 'all') {
      if (!member.is_verified) return false; // Only show verified users in default view
    }
    
    // Apply specific card filters
    if (activeFilter === 'pending') {
      if (member.is_verified || !member.invite_token) return false; // Show all invitations (active and expired)
    } else if (activeFilter === 'pending_email_verification') {
      if (member.is_verified || member.invite_token || member.invitation_expired) return false; // Only show pending email verification
    } else if (activeFilter === 'unverified') {
      if (member.is_verified || member.invite_token) return false; // Only show unverified registered users
    } else if (activeFilter === 'disabled') {
      return false; // Disabled users are handled separately
    } else if (activeFilter !== 'all') {
      if (!member.is_verified || member.role !== activeFilter) return false; // Only show verified users with specific role
    }
    
    // Then apply search term filtering across all columns
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in all relevant fields
    return (
      // Basic info
      (member.full_name && member.full_name.toLowerCase().includes(searchLower)) ||
      (member.email && member.email.toLowerCase().includes(searchLower)) ||
      (member.username && member.username.toLowerCase().includes(searchLower)) ||
      
      // Role and status
      (member.role && member.role.toLowerCase().includes(searchLower)) ||
      (member.is_verified !== undefined && String(member.is_verified).toLowerCase().includes(searchLower)) ||
      (member.is_disabled !== undefined && String(member.is_disabled).toLowerCase().includes(searchLower)) ||
      (member.approval_status && member.approval_status.toLowerCase().includes(searchLower)) ||
      
      // Department and team info
      (member.department && member.department.toLowerCase().includes(searchLower)) ||
      (member.team_lead && member.team_lead.toLowerCase().includes(searchLower)) ||
      
      // Additional fields that might exist
      (member.phone && member.phone.toLowerCase().includes(searchLower)) ||
      (member.location && member.location.toLowerCase().includes(searchLower)) ||
      (member.skills && Array.isArray(member.skills) && member.skills.some(skill => skill.toLowerCase().includes(searchLower))) ||
      (member.tags && Array.isArray(member.tags) && member.tags.some(tag => tag.toLowerCase().includes(searchLower)))
    );
  });

  // Handle disabled users filtering and search
  const filteredDisabledUsers = disabledUsers.filter(member => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search in all relevant fields
    return (
      // Basic info
      (member.full_name && member.full_name.toLowerCase().includes(searchLower)) ||
      (member.email && member.email.toLowerCase().includes(searchLower)) ||
      (member.username && member.username.toLowerCase().includes(searchLower)) ||
      
      // Role and status
      (member.role && member.role.toLowerCase().includes(searchLower)) ||
      (member.is_verified !== undefined && String(member.is_verified).toLowerCase().includes(searchLower)) ||
      (member.is_disabled !== undefined && String(member.is_disabled).toLowerCase().includes(searchLower)) ||
      (member.approval_status && member.approval_status.toLowerCase().includes(searchLower)) ||
      
      // Department and team info
      (member.department && member.department.toLowerCase().includes(searchLower)) ||
      (member.team_lead && member.team_lead.toLowerCase().includes(searchLower)) ||
      
      // Additional fields that might exist
      (member.phone && member.phone.toLowerCase().includes(searchLower)) ||
      (member.location && member.location.toLowerCase().includes(searchLower)) ||
      (member.skills && Array.isArray(member.skills) && member.skills.some(skill => skill.toLowerCase().includes(searchLower))) ||
      (member.tags && Array.isArray(member.tags) && member.tags.some(tag => tag.toLowerCase().includes(searchLower)))
    );
  });

  // Pagination calculations
  const membersToShow = activeFilter === 'disabled' ? filteredDisabledUsers : filteredMembers;
  const totalPages = Math.ceil(membersToShow.length / pageSize);
  const paginatedMembers = membersToShow.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Reset to first page when search change or filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeFilter]);

  // Handle card filter clicks
  const handleCardFilter = (filter) => {
    setActiveFilter(filter);
    setSearchTerm(''); // Clear search when changing filter
  };

  // Data fetching is now handled by React Query hooks

  const handleAddEmail = (e) => {
    e.preventDefault && e.preventDefault();
    const email = newEmail.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return;
    if (!emailRegex.test(email)) {
      setEmailError("Invalid email address");
      return;
    }
    if (inviteData.emails.includes(email)) {
      setEmailError("Email already added");
      return;
    }
    setInviteData(data => ({ ...data, emails: [...data.emails, email] }));
    setNewEmail("");
    setEmailError("");
  };

  const handleEmailInputKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      handleAddEmail(e);
    }
  };

  const handlePasteEmails = (e) => {
    const paste = e.clipboardData.getData("text");
    const emails = paste.split(/[\s,]+/).map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let added = false;
    emails.forEach(email => {
      if (emailRegex.test(email) && !inviteData.emails.includes(email)) {
        setInviteData(data => ({ ...data, emails: [...data.emails, email] }));
        added = true;
      }
    });
    if (added) setEmailError("");
    setTimeout(() => setNewEmail(""), 0);
    e.preventDefault();
  };

  const handleRemoveEmail = (email) => {
    setInviteData(data => ({ ...data, emails: data.emails.filter(e => e !== email) }));
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (inviteData.emails.length === 0) {
      toast.error('Please enter at least one valid email.');
      return;
    }
    if (inviteData.roles.length === 0) {
      toast.error('Please select a role.');
      return;
    }
    setIsInviting(true);
    try {
      await inviteUserMutation.mutateAsync({ emails: inviteData.emails, roles: inviteData.roles });
      toast.success('Invitations sent successfully');
      await refreshTeamLists();
      setShowInviteModal(false);
      setInviteData({ emails: [], roles: ['member'] });
      setNewEmail("");
      setEmailError("");
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to invite team member'));
    } finally {
      setIsInviting(false);
    }
  };

  const updateRole = async (userId, newRole) => {
    setIsUpdatingRole(true);
    try {
      await updateUserRoleMutation.mutateAsync({ userId, role: newRole });
      toast.success('Role updated successfully');
      await refreshTeamLists();
    } catch (error) {
      toast.error('Failed to update role');
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const toggleUserStatus = async (userId) => {
    // Find member from the appropriate list based on active filter
    const member = activeFilter === 'disabled' 
      ? disabledUsers.find(m => m.id === userId)
      : teamMembers.find(m => m.id === userId);
    
    const isDisabled = member?.is_disabled;
    const action = isDisabled ? 'enable' : 'disable';
    
    setConfirmModal({
      show: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
      message: `Are you sure you want to ${action} ${member?.full_name || member?.email}?`,
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      confirmButtonStyle: action === 'enable' ? 'success' : 'danger',
      onConfirm: async () => {
        setTogglingUserId(userId);
        try {
          await toggleUserStatusMutation.mutateAsync({ userId, action });
          toast.success(`User ${action}d successfully`);
          await refreshTeamLists();
        } catch (error) {
          toast.error(`Failed to ${action} user`);
        } finally {
          setTogglingUserId(null);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const handleUserApproval = async (userId, action) => {
    const actionText = action === 'approve' ? 'approve' : 'decline';
    const pendingUser = pendingUsers.find(u => u.id === userId);
    const userName = pendingUser?.full_name || pendingUser?.email;
    
    setConfirmModal({
      show: true,
      title: `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} User`,
      message: `Are you sure you want to ${actionText} ${userName}?`,
      confirmText: actionText.charAt(0).toUpperCase() + actionText.slice(1),
      confirmButtonStyle: action === 'approve' ? 'success' : 'danger',
      onConfirm: async () => {
        setApprovingUserId(userId);
        try {
          await approveUserMutation.mutateAsync({ userId, action });
          toast.success(`User ${actionText}d successfully`);
          await refreshTeamLists();
        } catch (error) {
          toast.error(`Failed to ${actionText} user`);
        } finally {
          setApprovingUserId(null);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const handleReapproval = async (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    const userName = member?.full_name || member?.email;
    
    setConfirmModal({
      show: true,
      title: 'Re-approve User',
      message: `Are you sure you want to re-approve ${userName}? This will allow them to log in again.`,
      confirmText: 'Re-approve',
      confirmButtonStyle: 'success',
      onConfirm: async () => {
        setReapprovingUserId(userId);
        try {
          await reapproveUserMutation.mutateAsync(userId);
          toast.success('User re-approved successfully');
          await refreshTeamLists();
        } catch (error) {
          toast.error('Failed to re-approve user');
        } finally {
          setReapprovingUserId(null);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const handleResendInvitation = async (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    const userName = member?.full_name || member?.email;
    
    setConfirmModal({
      show: true,
      title: 'Resend Invitation',
      message: `Are you sure you want to resend an invitation to ${userName}? This will generate a new invitation link that expires in 1 day.`,
      confirmText: 'Resend Invitation',
      confirmButtonStyle: 'info',
      onConfirm: async () => {
        try {
          setIsResending(true);
          await resendInvitationMutation.mutateAsync(userId);
          toast.success('Invitation resent successfully');
          await refreshTeamLists();
        } catch (error) {
          const errorMessage = error?.response?.data?.detail || 'Failed to resend invitation';
          toast.error(errorMessage);
        } finally {
          setIsResending(false);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const handleResendVerification = async (userId) => {
    const member = teamMembers.find(m => m.id === userId);
    const userName = member?.full_name || member?.email;
    
    setConfirmModal({
      show: true,
      title: 'Resend Verification Email',
      message: `Are you sure you want to resend a verification email to ${userName}?`,
      confirmText: 'Resend Email',
      confirmButtonStyle: 'info',
      onConfirm: async () => {
        try {
          setIsResending(true);
          await resendVerificationMutation.mutateAsync(userId);
          toast.success('Verification email sent successfully');
          await refreshTeamLists();
        } catch (error) {
          const errorMessage = error?.response?.data?.detail || 'Failed to resend verification email';
          toast.error(errorMessage);
        } finally {
          setIsResending(false);
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'project_lead':
        return <Shield className="h-4 w-4 text-blue-600" />;
      default:
        return <User className="h-4 w-4 text-secondary-600" />;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-yellow-100 text-yellow-800',
      project_lead: 'bg-blue-100 text-blue-800',
      member: 'bg-secondary-100 text-secondary-600'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[role]}`}>
        {role === 'project_lead' ? 'Project Lead' : role.charAt(0).toUpperCase() + role.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (user?.role !== 'admin' && user?.role !== 'project_lead') {
    return (
      <div className="text-center py-12">
        <Users className="h-16 w-16 text-secondary-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900 mb-2">Access Restricted</h3>
        <p className="text-secondary-600">You don't have permission to view team management</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="text-xl font-bold text-secondary-900 dark:text-white">Team Management</h1>
                  {(user?.role === 'admin' || user?.role === 'project_lead') && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <UserPlus className="h-4 w-4" />
            Invite Member
          </button>
        )}
        </div>

        {/* Team Stats */}
        <div className="grid grid-cols-1 md:grid-cols-7 gap-6">
          <div 
            className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              activeFilter === 'all' ? 'ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
            onClick={() => handleCardFilter('all')}
          >
            <Users className="h-8 w-8 text-primary-600 mx-auto mb-2" />
            <div className="text-xl font-bold text-secondary-900 dark:text-white">
              {verifiedUsers.length}
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Users</div>
          </div>
          
          <div 
            className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              activeFilter === 'admin' ? 'ring-2 ring-yellow-500 bg-yellow-50 dark:bg-yellow-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
            onClick={() => handleCardFilter('admin')}
          >
            <Crown className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
            <div className="text-xl font-bold text-secondary-900 dark:text-white">
              {verifiedUsers.filter(m => m.role === 'admin').length}
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Admins</div>
          </div>
          
          <div 
            className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              activeFilter === 'project_lead' ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
            onClick={() => handleCardFilter('project_lead')}
          >
            <Shield className="h-8 w-8 text-blue-600 mx-auto mb-2" />
            <div className="text-xl font-bold text-secondary-900 dark:text-white">
              {verifiedUsers.filter(m => m.role === 'project_lead').length}
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Project Leads</div>
          </div>
          
          <div 
            className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              activeFilter === 'member' ? 'ring-2 ring-green-500 bg-green-50 dark:bg-green-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
            onClick={() => handleCardFilter('member')}
          >
            <User className="h-8 w-8 text-green-600 mx-auto mb-2" />
            <div className="text-xl font-bold text-secondary-900 dark:text-white">
              {verifiedUsers.filter(m => m.role === 'member').length}
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Members</div>
          </div>
          
          <div 
            className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              activeFilter === 'pending' ? 'ring-2 ring-orange-500 bg-orange-50 dark:bg-orange-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
            onClick={() => handleCardFilter('pending')}
          >
            <UserPlus className="h-8 w-8 text-orange-600 mx-auto mb-2" />
            <div className="text-xl font-bold text-secondary-900 dark:text-white">
              {pendingInvitations.length}
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Pending Invitations</div>
          </div>
          
          <div 
            className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
              activeFilter === 'pending_email_verification' ? 'ring-2 ring-purple-500 bg-purple-50 dark:bg-purple-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
            }`}
            onClick={() => handleCardFilter('pending_email_verification')}
          >
            <Mail className="h-8 w-8 text-purple-600 mx-auto mb-2" />
            <div className="text-xl font-bold text-secondary-900 dark:text-white">
              {pendingEmailVerification.length}
            </div>
            <div className="text-sm text-secondary-600 dark:text-secondary-400">Pending Email Verification</div>
          </div>
          
          {user?.role === 'admin' && (
            <div 
              className={`card text-center cursor-pointer transition-all duration-200 hover:shadow-md ${
                activeFilter === 'disabled' ? 'ring-2 ring-red-500 bg-red-50 dark:bg-red-900/30' : 'hover:bg-gray-50 dark:hover:bg-secondary-700'
              }`}
              onClick={() => handleCardFilter('disabled')}
            >
              <UserX className="h-8 w-8 text-red-600 mx-auto mb-2" />
              <div className="text-xl font-bold text-secondary-900 dark:text-white">
                {disabledUsers.length}
              </div>
              <div className="text-sm text-secondary-600 dark:text-secondary-400">Disabled Users</div>
            </div>
          )}
        </div>

        {/* Search Section */}
        <div className="card">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                placeholder="Search across all columns..."
                className="input-field pl-10"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.target.blur();
                  }
                }}
              />
            </div>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-secondary-400 hover:text-secondary-600"
                title="Clear search"
              >
                <XIcon className="h-4 w-4" />
              </button>
            )}
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setCurrentPage(1);
                }}
                className="btn-secondary text-sm"
                title="Clear search"
              >
                Clear Search
              </button>
            )}
          </div>

          {/* Results summary */}
          <div className="mt-4 flex items-center justify-between text-sm text-secondary-600">
            <div>
              Showing {membersToShow.length} of {activeFilter === 'disabled' ? disabledUsers.length : (activeFilter === 'all' ? verifiedUsers.length : teamMembers.length)} team members
              {activeFilter !== 'all' && (
                <span className="ml-2 px-2 py-1 bg-primary-100 text-primary-700 rounded-full text-xs">
                  Filtered by: {activeFilter === 'pending' ? 'Pending Invitations' : 
                    activeFilter === 'pending_email_verification' ? 'Pending Email Verification' :
                    activeFilter === 'admin' ? 'Admins' : 
                    activeFilter === 'project_lead' ? 'Project Leads' : 
                    activeFilter === 'member' ? 'Members' : 
                    activeFilter === 'disabled' ? 'Disabled Users' : 'All'}
                </span>
              )}
            </div>
            {activeFilter !== 'all' && (
              <button
                onClick={() => handleCardFilter('all')}
                className="text-primary-600 hover:text-primary-800 font-medium"
              >
                Clear Filter
              </button>
            )}
          </div>
        </div>


        {/* Pending Approvals */}
        {(user?.role === 'admin' || user?.role === 'project_lead') && pendingUsers.length > 0 && (
          <div className="card">
            <h2 className="text-sm font-semibold mb-4">Pending Approvals</h2>
            <div className="space-y-4">
              {pendingUsers.map(pendingUser => (
                <div key={pendingUser.id} className="flex items-center justify-between p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-yellow-100 rounded-full flex items-center justify-center">
                      <User className="h-4 w-4 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-secondary-900">{pendingUser.full_name || pendingUser.email}</h3>
                      <p className="text-sm text-secondary-600">{pendingUser.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                          Pending Approval
                        </span>
                        {pendingUser.oauth_provider && (
                          <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-medium">
                            {pendingUser.oauth_provider === 'google' ? 'Google' : 'Microsoft'} OAuth
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Only show approve/decline buttons for admins */}
                  {user?.role === 'admin' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleUserApproval(pendingUser.id, 'approve')}
                        disabled={approvingUserId !== null}
                        className={`px-3 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-green-500 ${approvingUserId !== null ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 text-white hover:bg-green-700'}`}
                      >
                        {approvingUserId === pendingUser.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleUserApproval(pendingUser.id, 'decline')}
                        disabled={approvingUserId !== null}
                        className={`px-3 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-red-500 ${approvingUserId !== null ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}
                      >
                        {approvingUserId === pendingUser.id ? 'Declining...' : 'Decline'}
                      </button>
                    </div>
                  )}
                  
                 
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Team Members */}
        <div className="card">
          <h2 className="text-sm font-semibold mb-4">Team Members</h2>
          
          {membersToShow.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-600">
                {activeFilter === 'disabled' 
                  ? (disabledUsers.length === 0 ? 'No disabled users found' : 'No disabled users match your search')
                  : (teamMembers.length === 0 ? 'No team members found' : 'No team members match your search')
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {paginatedMembers.map(member => (
                <div key={member.id} className={`flex items-center justify-between p-4 border rounded-lg ${
                  activeFilter === 'disabled'
                    ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                    : member.is_verified 
                    ? 'border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800' 
                    : member.invitation_expired
                    ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                    : 'border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                      activeFilter === 'disabled'
                        ? 'bg-red-100'
                        : member.is_verified 
                        ? 'bg-primary-100' 
                        : member.invitation_expired
                        ? 'bg-red-100'
                        : 'bg-yellow-100'
                    }`}>
                      {activeFilter === 'disabled' ? <UserX className="h-4 w-4 text-red-600" /> : getRoleIcon(member.role)}
                    </div>
                                         <div>
                       <h3 className="text-sm font-medium text-secondary-900 dark:text-white">{member.full_name || member.email}</h3>
                       <p className="text-sm text-secondary-600 dark:text-secondary-400">{member.email}</p>
                       <NowPlaying userId={member.id} className="mt-1" />
                      {activeFilter === 'disabled' ? (
                        <span className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium mt-1">
                          Disabled
                        </span>
                      ) : member.is_verified ? (
                        <span className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-medium mt-1">
                          Verified
                        </span>
                      ) : member.invite_token ? (
                        member.invitation_expired ? (
                          <span className="inline-block bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-medium mt-1">
                            Invitation Expired
                          </span>
                        ) : (
                          <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium mt-1">
                            Invitation Sent
                          </span>
                        )
                      ) : (
                        <span className="inline-block bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium mt-1">
                          Email Verification Pending
                        </span>
                      )}
                      {member.approval_status === 'declined' && (
                        <span className="inline-block bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs font-medium mt-1">
                          Declined
                        </span>
                      )}
                      {!member.is_verified && member.invitation_expires_at && (
                        <p className="text-xs text-gray-500 mt-1">
                          Expires: {new Date(member.invitation_expires_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {getRoleBadge(member.role)}
                    
                    {member.id !== user.id && (user?.role === 'admin' || (user?.role === 'project_lead' && member.invitation_expired)) && (
                      <div className="flex items-center gap-2">
                        {user?.role === 'admin' && member.approval_status !== 'declined' && (
                          <select
                            className="text-sm border border-secondary-300 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white rounded px-2 py-1"
                            value={member.role}
                            onChange={(e) => updateRole(member.id, e.target.value)}
                            disabled={member.is_disabled || isUpdatingRole}
                          >
                            <option value="member">Member</option>
                            <option value="project_lead">Project Lead</option>
                            <option value="admin">Admin</option>
                          </select>
                        )}
                        
                        {user?.role === 'admin' ? (
                          member.approval_status === 'declined' ? (
                            <button
                              onClick={() => handleReapproval(member.id)}
                              disabled={reapprovingUserId !== null}
                              className={`text-sm ${reapprovingUserId !== null ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:text-green-700'}`}
                            >
                              {reapprovingUserId === member.id ? 'Re-approving...' : 'Re-approve'}
                            </button>
                          ) : !member.is_verified && !member.invite_token ? (
                            <button
                              onClick={() => handleResendVerification(member.id)}
                              disabled={isResending}
                              className={`text-sm ${isResending ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
                            >
                              {isResending ? 'Sending...' : 'Resend Verification Email'}
                            </button>
                          ) : member.invitation_expired ? (
                            <button
                              onClick={() => handleResendInvitation(member.id)}
                              disabled={isResending}
                              className={`text-sm ${isResending ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
                            >
                              {isResending ? 'Sending...' : 'Resend Invitation'}
                            </button>
                          ) : activeFilter === 'disabled' ? (
                            <button
                              onClick={() => toggleUserStatus(member.id)}
                              disabled={togglingUserId !== null}
                              className={`text-sm ${togglingUserId !== null ? 'text-gray-400 cursor-not-allowed' : 'text-green-600 hover:text-green-700'}`}
                            >
                              {togglingUserId === member.id ? 'Enabling...' : 'Enable'}
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleUserStatus(member.id)}
                              disabled={togglingUserId !== null}
                              className={`text-sm ${togglingUserId !== null ? 'text-gray-400 cursor-not-allowed' : (member.is_disabled ? 'text-green-600 hover:text-green-700' : 'text-red-600 hover:text-red-700')}`}
                            >
                              {togglingUserId === member.id ? (member.is_disabled ? 'Enabling...' : 'Disabling...') : (member.is_disabled ? 'Enable' : 'Disable')}
                            </button>
                          )
                        ) : member.invitation_expired ? (
                          <button
                            onClick={() => handleResendInvitation(member.id)}
                            disabled={isResending}
                            className={`text-sm ${isResending ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700'}`}
                          >
                            {isResending ? 'Sending...' : 'Resend Invitation'}
                          </button>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination and Page Size */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
            {/* Page size selector - always visible */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-secondary-600">Show:</span>
              <select
                className="input-field w-20 text-sm"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-sm text-secondary-600">per page</span>
            </div>

            {/* Pagination controls - only show when multiple pages */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button 
                  className="btn-secondary px-3" 
                  disabled={currentPage === 1} 
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    className={`px-3 py-1 rounded transition-colors ${
                      currentPage === i + 1 
                        ? 'bg-primary-600 text-white' 
                        : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                    }`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  className="btn-secondary px-3" 
                  disabled={currentPage === totalPages} 
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            )}
            
            {/* Page info */}
            <div className="text-sm text-secondary-600 dark:text-secondary-400">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        </div>

      </div>

      {/* Invite Drawer */}
      <FormDrawer
        open={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setInviteData({ emails: [], roles: ['member'] });
          setNewEmail("");
          setEmailError("");
        }}
        title="Invite Team Members"
        subtitle="Send an invite to one or more teammates."
        icon={<UserPlus className="h-5 w-5" />}
        iconColor="primary"
        widthClass="sm:max-w-md lg:max-w-lg"
        footer={(
          <>
            <button
              type="button"
              onClick={() => {
                setShowInviteModal(false);
                setInviteData({ emails: [], roles: ['member'] });
                setNewEmail("");
                setEmailError("");
              }}
              className="btn-secondary"
              disabled={isInviting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="invite-form"
              disabled={isInviting}
              className="btn-primary min-w-[140px] justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isInviting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Sending…
                </span>
              ) : (
                'Send Invite'
              )}
            </button>
          </>
        )}
      >
            <form id="invite-form" onSubmit={handleInvite} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Emails *
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {inviteData.emails.map(email => (
                    <span key={email} className="inline-flex items-center bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 rounded px-2 py-1 text-xs font-medium">
                      {email}
                      <button type="button" className="ml-1 text-blue-600 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100" onClick={() => handleRemoveEmail(email)}>&times;</button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Add Emails"
                  value={newEmail}
                  onChange={e => { setNewEmail(e.target.value); setEmailError(""); }}
                  onKeyDown={handleEmailInputKeyDown}
                  onBlur={handleAddEmail}
                  onPaste={handlePasteEmails}
                  autoFocus
                />
                {emailError && <div className="text-xs text-red-600 dark:text-red-400 mt-1">{emailError}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Role *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-secondary-700 dark:text-secondary-300 cursor-pointer">
                    <input
                      type="radio"
                      name="role"
                      value="member"
                      checked={inviteData.roles[0] === 'member'}
                      onChange={e => {
                        setInviteData(data => ({
                          ...data,
                          roles: [e.target.value]
                        }));
                      }}
                      className="text-primary-600 focus:ring-primary-500"
                    />
                    Member
                  </label>
                  {user?.role === 'admin' && (
                    <>
                      <label className="flex items-center gap-2 text-secondary-700 dark:text-secondary-300 cursor-pointer">
                        <input
                          type="radio"
                          name="role"
                          value="project_lead"
                          checked={inviteData.roles[0] === 'project_lead'}
                          onChange={e => {
                            setInviteData(data => ({
                              ...data,
                              roles: [e.target.value]
                            }));
                          }}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        Project Lead
                      </label>
                      <label className="flex items-center gap-2 text-secondary-700 dark:text-secondary-300 cursor-pointer">
                        <input
                          type="radio"
                          name="role"
                          value="admin"
                          checked={inviteData.roles[0] === 'admin'}
                          onChange={e => {
                            setInviteData(data => ({
                              ...data,
                              roles: [e.target.value]
                            }));
                          }}
                          className="text-primary-600 focus:ring-primary-500"
                        />
                        Admin
                      </label>
                    </>
                  )}
                </div>
              </div>
            </form>
      </FormDrawer>

      {/* Confirm Modal */}
      <ConfirmModal
        open={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmButtonStyle={confirmModal.confirmButtonStyle}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
        isLoading={togglingUserId !== null || approvingUserId !== null || reapprovingUserId !== null || isResending}
      />
    </>
  );
};

export default TeamPage;